import type { CreateLoanInput, Loan } from '@taler/api-client'
import { entryKeys, loanKeys } from '@taler/api-client'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { problemDetail } from '../internal/problem.ts'

export interface UseLoansOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseLoans {
  readonly loans: ComputedRef<readonly Loan[]>
  hasHistory(loanId: string): boolean | undefined
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly isCreating: ComputedRef<boolean>
  readonly hasCreateError: ComputedRef<boolean>
  readonly createErrorDetail: ComputedRef<string | undefined>
  create(input: CreateLoanInput): Promise<Loan>
  retry(): void
}

export function useLoans(options: UseLoansOptions): UseLoans {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const loansQuery = useQuery({
    queryKey: computed(() => loanKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.loans.list(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const undrawnCandidates = computed(() =>
    (loansQuery.data.value ?? []).filter(
      (loan) =>
        loan.outstandingPrincipal.units === 0n &&
        loan.accruedInterest.units === 0n &&
        loan.accrualCount === 0,
    ),
  )

  const historyProbes = useQueries({
    queries: computed(() =>
      undrawnCandidates.value.map((loan) => {
        const query = { accountId: loan.principalAccountId, limit: 1 }
        return {
          queryKey: entryKeys.list(options.ledgerId() ?? '', query),
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            api.entries.list(options.ledgerId()!, query, signal),
          retry: false,
        }
      }),
    ),
  })

  const historyByLoanId = computed(
    () =>
      new Map(
        undrawnCandidates.value.map((loan, index) => {
          const page = historyProbes.value[index]?.data
          return [loan.id, page === undefined ? undefined : page.items.length > 0] as const
        }),
      ),
  )

  const createMutation = useMutation({
    mutationFn: (input: CreateLoanInput) => api.loans.create(options.ledgerId()!, input),
    onSuccess: () => {
      for (const key of loanKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  return {
    loans: computed(() => loansQuery.data.value ?? []),
    hasHistory: (loanId) => historyByLoanId.value.get(loanId),
    isLoading: computed(() => active.value && loansQuery.isPending.value),
    error: computed(() => loansQuery.error.value),
    retrying: retrying(loansQuery),
    isCreating: computed(() => createMutation.isPending.value),
    hasCreateError: computed(() => createMutation.error.value !== null),
    createErrorDetail: computed(() => problemDetail(createMutation.error.value)),
    create: (input) => createMutation.mutateAsync(input),
    retry: () => {
      void loansQuery.refetch()
    },
  }
}
